/**
 * ServiceRequiredDocs — reusable widget for any service page.
 *
 * Shows every document from the MASTER list and lets the operator toggle
 * "مطلوب / غير مطلوب" per service. Name + description stay live-linked to
 * master (read-only here). Only the per-service `is_required` is editable.
 *
 * Usage:
 *   <ServiceRequiredDocs serviceId="visa_visit" serviceLabel="تأشيرة زيارة" />
 *
 * Backend:
 *   GET  /api/services/{serviceId}/required-docs
 *   PUT  /api/services/{serviceId}/required-docs/{docId}  body: {is_required}
 *   DELETE /api/services/{serviceId}/required-docs/overrides
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, FileText, FileImage, Files, ListChecks, RefreshCw, RotateCcw, AlertCircle,
  CheckCircle2, XCircle, Sparkles, Globe,
} from 'lucide-react';
import { Switch } from './ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File-type metadata — must match MasterDocuments.js so the badge style is
// consistent across the admin app.
const FILE_TYPE_META = {
  pdf:   { ar: 'PDF فقط',     en: 'PDF only',     icon: FileText,  cls: 'bg-red-500/10 border-red-500/30 text-red-300' },
  image: { ar: 'صورة فقط',    en: 'Image only',   icon: FileImage, cls: 'bg-blue-500/10 border-blue-500/30 text-blue-300' },
  both:  { ar: 'PDF أو صورة', en: 'PDF or Image', icon: Files,     cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' },
  file:  { ar: 'PDF أو صورة', en: 'PDF or Image', icon: Files,     cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' },
};

export function ServiceRequiredDocs({ serviceId, serviceLabel, serviceLabelEn, emirate = null, emirateLabel = '', reloadKey = 0, onChanged }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const q = emirate ? `?emirate=${emirate}` : '';
  const [docs, setDocs] = useState([]);
  const [residentId, setResidentId] = useState(null);  // { enabled, selected_nationalities[] }
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [bulking, setBulking] = useState(false);
  // { kind: 'select-all' | 'clear-all' } — controls the confirmation dialog.
  const [confirmAction, setConfirmAction] = useState(null);

  const load = useCallback(async () => {
    if (!serviceId) return;
    setLoading(true);
    try {
      // Run both fetches in parallel — the resident-id option is global and
      // applies to every service, so we show it as a pinned row at the top.
      const [docsRes, ridRes] = await Promise.all([
        axios.get(`${API_URL}/api/services/${serviceId}/required-docs${q}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/master-documents/resident-id-option`, { headers: getAuthHeaders() }).catch(() => null),
      ]);
      setDocs(docsRes.data?.docs || []);
      if (ridRes?.data?.success) {
        setResidentId(ridRes.data.data || null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل القائمة' : 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [serviceId, q, reloadKey, isAr]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const toggle = async (doc, nextValue) => {
    setTogglingId(doc.id);
    try {
      await axios.put(
        `${API_URL}/api/services/${serviceId}/required-docs/${doc.id}${q}`,
        { is_required: nextValue },
        { headers: getAuthHeaders() },
      );
      setDocs((prev) =>
        prev.map((d) =>
          d.id === doc.id ? { ...d, is_required: nextValue, is_overridden: nextValue } : d,
        ),
      );
      onChanged?.();
      toast.success(
        isAr
          ? `${doc.name_ar} — ${nextValue ? '🟢 مطلوب' : '⚪ غير مطلوب'}`
          : `${doc.name_en || doc.name_ar} — ${nextValue ? '🟢 Required' : '⚪ Not required'}`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل التحديث' : 'Update failed'));
    } finally {
      setTogglingId(null);
    }
  };

  const bulkSet = async (nextValue) => {
    setBulking(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/services/${serviceId}/required-docs/bulk${q}`,
        { all: true, is_required: nextValue },
        { headers: getAuthHeaders() },
      );
      toast.success(
        isAr
          ? `تم تحديث ${res.data?.affected || 0} وثيقة`
          : `Updated ${res.data?.affected || 0} document(s)`,
      );
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBulking(false);
    }
  };

  const resetAll = async () => {
    setResetting(true);
    try {
      await axios.delete(
        `${API_URL}/api/services/${serviceId}/required-docs/overrides${q}`,
        { headers: getAuthHeaders() },
      );
      toast.success(isAr ? 'تم مسح كل التخصيصات' : 'All overrides cleared');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setResetting(false);
    }
  };

  const runConfirmed = async () => {
    if (!confirmAction) return;
    const action = confirmAction;
    setConfirmAction(null);
    if (action.kind === 'select-all') await bulkSet(true);
    else if (action.kind === 'clear-all') await resetAll();
  };

  const requiredCount = docs.filter((d) => d.is_required).length;

  return (
    <div className="space-y-4" data-testid={`service-required-docs-${serviceId}${emirate ? `-${emirate}` : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center">
            <ListChecks className="h-5 w-5 text-[#d4af37]" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white" data-testid="srd-title">
              {emirateLabel
                ? (isAr ? `الأوراق المطلوبة — ${emirateLabel}` : `Required documents — ${emirateLabel}`)
                : (isAr ? 'الأوراق المطلوبة لهذه الخدمة' : 'Required documents for this service')}
            </h3>
            <p className="text-xs text-[#A1A1AA] mt-0.5">
              {(isAr ? serviceLabel : (serviceLabelEn || serviceLabel)) || serviceId}
              {' · '}
              <span className="text-emerald-300">{requiredCount}</span>
              {' / '}
              {docs.length} {isAr ? 'مفعّلة' : 'enabled'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-2 rounded-lg bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-white"
            title={isAr ? 'تحديث' : 'Refresh'}
            data-testid="srd-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setConfirmAction({ kind: 'select-all' })}
            disabled={bulking || loading || docs.length === 0}
            className="px-3 py-2 text-xs rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-50 flex items-center gap-2"
            title={isAr ? 'تفعيل الجميع كـ "مطلوب"' : 'Enable all as "Required"'}
            data-testid="srd-select-all"
          >
            {bulking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            {isAr ? 'اختر الكل' : 'Select all'}
          </button>
          <button
            onClick={() => setConfirmAction({ kind: 'clear-all' })}
            disabled={resetting}
            className="px-3 py-2 text-xs rounded-lg bg-white/5 border border-white/15 text-[#A1A1AA] hover:bg-white/10 hover:text-white disabled:opacity-50 flex items-center gap-2"
            title={isAr ? 'مسح التخصيصات — لا شيء مطلوب' : 'Clear all — nothing required'}
            data-testid="srd-reset"
          >
            {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            {isAr ? 'مسح الكل' : 'Clear all'}
          </button>
        </div>
      </div>

      {/* Live-link banner */}
      <div className="bg-blue-500/[0.06] border border-blue-500/30 rounded-lg p-3 flex items-start gap-2 text-xs text-blue-200">
        <Sparkles className="h-4 w-4 shrink-0 mt-0.5 text-blue-300" />
        <div>
          {emirate
            ? (isAr
              ? 'هذه القائمة خاصة بالإمارة المحددة فقط. الأسماء والأوصاف مرتبطة بقائمة الأوراق المركزية (Master). استخدم "نسخ الأوراق" لتطبيق نفس الأوراق على إمارات أخرى.'
              : 'This list applies to the selected emirate only. Names and descriptions are live-linked to the Master list. Use "copy documents" to apply the same set to other emirates.')
            : (isAr
              ? 'اختر الأوراق المطلوبة لهذه الخدمة فقط. الأسماء والأوصاف مرتبطة بقائمة الأوراق المركزية (Master) — أي تعديل في Master ينعكس هنا تلقائياً. الافتراضي: لا شيء مطلوب حتى تُفعّله بنفسك.'
              : 'Pick only the documents required for this service. Names and descriptions are live-linked to the Master list. Default: nothing is required until you toggle it on.')}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-[#d4af37]" />
        </div>
      ) : docs.length === 0 ? (
        <div className="text-center py-12 text-[#71717A] bg-white/[0.02] rounded-xl border border-white/10">
          <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p>{isAr ? 'لا توجد أوراق في القائمة المركزية بعد' : 'No documents in Master list yet'}</p>
          <p className="text-[10px] mt-1">
            {isAr ? 'ادخل قائمة الأوراق المطلوبة → ستضاف الافتراضيات تلقائياً' : 'Open the Master list — defaults are auto-added'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          {/* Pinned row: Resident ID Option (Master setting, applies globally) */}
          {residentId?.enabled && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border p-3.5 bg-violet-500/[0.06] border-violet-500/40 md:col-span-2"
              data-testid="srd-resident-id-row"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-violet-500/20 text-violet-300 flex items-center justify-center shrink-0">
                  <Globe className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-white" dir="rtl">
                    {isAr ? 'الهوية الصادرة من دولة المقيم' : 'Resident Country ID'}
                  </h4>
                  <p className="text-[11px] text-violet-200/80 mt-1">
                    {isAr
                      ? `مطلوبة لـ ${residentId.selected_nationalities?.length || 0} جنسية محددة في Master.`
                      : `Required for ${residentId.selected_nationalities?.length || 0} selected nationalities from Master.`}
                  </p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-violet-500/15 text-violet-200 border border-violet-500/40 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      {isAr ? 'مرتبط بـ Master' : 'Linked to Master'}
                    </span>
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/30">
                      {residentId.selected_nationalities?.length || 0} {isAr ? 'جنسية' : 'nationalities'}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {docs.map((d) => {
            const isOn = d.is_required;
            const pending = togglingId === d.id;
            const ft = FILE_TYPE_META[d.document_type] || FILE_TYPE_META.both;
            const FtIcon = ft.icon;
            return (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl border p-3.5 transition-all ${
                  isOn
                    ? 'bg-emerald-500/[0.05] border-emerald-500/30'
                    : 'bg-white/[0.02] border-white/10'
                }`}
                data-testid={`srd-row-${d.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    isOn ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/5 text-white/40'
                  }`}>
                    <FileText className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className={`text-sm font-bold truncate ${isOn ? 'text-white' : 'text-white/70'}`} dir="rtl">
                          {isAr ? d.name_ar : (d.name_en || d.name_ar)}
                        </h4>
                        {(d.description_ar || d.description_en) && (
                          <p className="text-[11px] text-[#A1A1AA] mt-1 line-clamp-1">
                            {isAr ? d.description_ar : (d.description_en || d.description_ar)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {pending && <Loader2 className="h-4 w-4 animate-spin text-[#d4af37]" />}
                        <Switch
                          checked={isOn}
                          disabled={pending}
                          onCheckedChange={(next) => toggle(d, next)}
                          className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-white/15"
                          data-testid={`srd-switch-${d.id}`}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`px-2 py-0.5 text-[10px] rounded-full border flex items-center gap-1 ${
                        isOn
                          ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                          : 'bg-white/[0.03] text-white/40 border-white/10'
                      }`}>
                        {isOn
                          ? <><CheckCircle2 className="h-3 w-3" />{isAr ? 'مطلوب' : 'Required'}</>
                          : <><XCircle className="h-3 w-3" />{isAr ? 'غير مطلوب' : 'Not required'}</>}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-[10px] rounded-full border flex items-center gap-1 ${ft.cls}`}
                        data-testid={`srd-filetype-${d.id}`}
                      >
                        <FtIcon className="h-3 w-3" />
                        {isAr ? ft.ar : ft.en}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Bulk-action confirmation dialog (replaces window.confirm) */}
      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent
          className="bg-[#09090b] border-white/10 text-white"
          data-testid="srd-confirm-dialog"
          dir={isAr ? 'rtl' : 'ltr'}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.kind === 'select-all'
                ? (isAr ? 'تفعيل جميع الوثائق؟' : 'Enable all documents?')
                : (isAr ? 'مسح جميع التخصيصات؟' : 'Clear all overrides?')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {confirmAction?.kind === 'select-all'
                ? (isAr
                    ? 'سيتم تعيين كل الوثائق من قائمة Master كـ "مطلوب" لهذه الخدمة.'
                    : 'Every document in the Master list will be marked as required for this service.')
                : (isAr
                    ? 'سيتم إرجاع كل الوثائق للحالة الافتراضية (لا شيء مطلوب).'
                    : 'All documents will return to the default (nothing required).')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              data-testid="srd-confirm-cancel"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runConfirmed}
              className={
                confirmAction?.kind === 'select-all'
                  ? 'bg-emerald-500 hover:bg-emerald-500/90 text-black font-bold'
                  : 'bg-white/10 border border-white/20 hover:bg-white/15 text-white font-bold'
              }
              data-testid="srd-confirm-ok"
            >
              {confirmAction?.kind === 'select-all'
                ? (isAr ? 'تفعيل الكل' : 'Enable all')
                : (isAr ? 'مسح الكل' : 'Clear all')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
